import React from 'react';
import { CheckCircle2, Users, Briefcase, Target, TrendingUp } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      number: '1',
      title: 'Choose a Service',
      description: 'Select the package that fits your job search needs and goals.',
      color: 'teal'
    },
    {
      number: '2',
      title: 'Complete Intake Survey',
      description: 'Share your experience, goals, and job preferences through our detailed intake form.',
      color: 'amber'
    },
    {
      number: '3',
      title: 'We Apply For You',
      description: 'Our team customizes your résumé and cover letter for each role and submits applications professionally.',
      color: 'blue'
    },
    {
      number: '4',
      title: 'We Track and Follow-Up',
      description: 'Receive tracking updates and optional follow-up support to maintain visibility with employers.',
      color: 'green'
    }
  ];

  const clarifications = [
    {
      icon: Users,
      title: 'We Are Not an Employment Agency',
      description: 'We don\'t represent employers or place candidates. We support your job search strategy and execution.'
    },
    {
      icon: Briefcase,
      title: 'You Choose the Roles',
      description: 'You provide the job links or postings. We complete each application accurately and professionally on your behalf.'
    },
    {
      icon: Target,
      title: 'Job Search Is Optional',
      description: 'Want help finding roles? Job sourcing is available as an add-on and can be added anytime.'
    },
    {
      icon: TrendingUp,
      title: 'Results Vary',
      description: 'Outcomes depend on role, market conditions, and experience level. Every search follows a different timeline.'
    }
  ];

  const colorMap = {
    teal: 'bg-teal-700',
    amber: 'bg-amber-600',
    blue: 'bg-blue-700',
    green: 'bg-green-700'
  };

  return (
    <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
            Simple Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            How Our Service Works
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Simple. Structured. Human.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-teal-300 hover:shadow-lg transition-all h-full">
                <div className={`w-16 h-16 ${colorMap[step.color]} rounded-xl flex items-center justify-center text-white font-bold text-2xl mb-4`}>
                  {step.number}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
              {/* Connector Arrow */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-gray-300">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Every Application Includes */}
        <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 md:p-12 border border-teal-200 mb-16">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Every Application Includes
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Tailored résumé per role',
              'Customized cover letter',
              'ATS-optimized formatting',
              'Full application completion',
              'Accuracy & alignment review',
              'Job application tracking chart',
              'No rushed clicks',
              'No copy-paste chaos'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle2 className="text-teal-700 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Clarifications */}
        <div className="grid md:grid-cols-2 gap-8">
          {clarifications.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="text-gray-700" size={24} />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h4>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Important Note */}
        <div className="mt-12 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
          <div className="flex items-start space-x-4">
            <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold">
              !
            </div>
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-2">No Guaranteed Outcomes</h4>
              <p className="text-gray-700">
                Interview and offer decisions are made by employers. We can't guarantee results—but we can control quality and strategy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;